<template>
  <div class="container-fluid">
    <div class="block-header">
      <bread-crumb :breadCrumbIteams="breadCrumbIteams"></bread-crumb>
    </div>

    <div class="row clearfix">
      <div class="col-12">
        <div class="card card-top-line">
          <div class="header d-flex justify-content-between align-items-center">
            <h2>Transaction Detail</h2>
            <b-button variant="outline-secondary" size="sm" @click="goBack">
              <i class="fa fa-arrow-left mr-1"></i> Back
            </b-button>
          </div>
          <div class="body pt-2">
            <div v-if="loading" class="text-center py-4">
              <b-spinner variant="primary"></b-spinner>
              <div class="text-muted mt-2">Loading transaction…</div>
            </div>
            <div v-else-if="error" class="alert alert-danger">
              <i class="fa fa-exclamation-circle mr-1"></i> {{ error }}
              <b-button variant="outline-danger" size="sm" class="ml-3" @click="loadDetail">
                Retry
              </b-button>
            </div>
            <template v-else-if="transaction">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <small class="text-muted d-block">Transaction ID</small>
                  <strong>{{ transaction.transactionId }}</strong>
                </div>
                <div class="col-md-6 mb-3">
                  <small class="text-muted d-block">Reference</small>
                  <strong>{{ transaction.referenceId || "—" }}</strong>
                </div>
                <div class="col-md-6 mb-3">
                  <small class="text-muted d-block">Status</small>
                  <span class="badge" :class="statusClass">
                    {{ transaction.status }}
                  </span>
                </div>
                <div class="col-md-6 mb-3">
                  <small class="text-muted d-block">Type</small>
                  <strong>{{ transaction.type || "—" }}</strong>
                </div>
                <div class="col-md-6 mb-3">
                  <small class="text-muted d-block">Source Amount</small>
                  <strong>
                    {{ formatAmount(transaction.amount?.source) }}
                    {{ transaction.amount?.sourceCurrency }}
                  </strong>
                </div>
                <div class="col-md-6 mb-3">
                  <small class="text-muted d-block">Destination Amount</small>
                  <strong>
                    {{ formatAmount(transaction.amount?.destination) }}
                    {{ transaction.amount?.destCurrency }}
                  </strong>
                </div>
                <div class="col-md-6 mb-3">
                  <small class="text-muted d-block">Created At</small>
                  <strong>{{ formatDateTime(transaction.timestamps?.createdAt) }}</strong>
                </div>
                <div class="col-md-6 mb-3">
                  <small class="text-muted d-block">Source (Merchant Sender)</small>
                  <strong>{{ transaction.source || "—" }}</strong>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { GET_BUSINESS_TRANSACTION_DETAIL } from "@/store/actions.type";

const STATUS_BADGE = {
  "Recipient Paid": "badge-success",
  "Funds Received": "badge-info",
  Processing: "badge-info",
  "Sent for Payment": "badge-info",
  "Awaiting Funds": "badge-warning",
  "In Review": "badge-warning",
  Incomplete: "badge-secondary",
  Failed: "badge-danger",
  Cancelled: "badge-danger",
  Refunded: "badge-dark",
};

export default {
  name: "BusinessTransactionDetail",
  components: {
    "bread-crumb": () => import("@/components/BreadCrumb.vue"),
  },
  data() {
    return {
      transaction: null,
      loading: false,
      error: null,
    };
  },
  computed: {
    businessId() {
      return this.$route.params.businessid;
    },
    transactionId() {
      return this.$route.params.transactionId;
    },
    breadCrumbIteams() {
      return [
        { title: "Business", links: "/business/list" },
        { title: "Financials", links: `/business/${this.businessId}/financials` },
        { title: "Transaction", links: "" },
      ];
    },
    statusClass() {
      return STATUS_BADGE[this.transaction?.status] || "badge-secondary";
    },
  },
  created() {
    this.loadDetail();
  },
  methods: {
    async loadDetail() {
      this.loading = true;
      this.error = null;
      try {
        const response = await this.$store.dispatch(
          GET_BUSINESS_TRANSACTION_DETAIL,
          {
            businessid: this.businessId,
            transactionId: this.transactionId,
          },
        );
        if (response?.data?.code === "1005-000") {
          this.transaction = response.data.details;
        } else {
          this.error =
            response?.data?.message || "Failed to load transaction detail.";
        }
      } catch (err) {
        this.error =
          err?.response?.data?.message || "Failed to load transaction detail.";
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.push(`/business/${this.businessId}/financials`);
    },
    formatAmount(value) {
      if (value == null) return "—";
      const n = Number(value);
      if (Number.isNaN(n)) return value;
      return n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDateTime(value) {
      if (!value) return "—";
      const d = new Date(value);
      return Number.isNaN(d.getTime())
        ? "—"
        : d.toLocaleString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
    },
  },
};
</script>
